/**
 * Запись голоса прямо в строке ввода — перенос useVoiceCapture из ГиперЧАТа.
 *
 * 🔴 Запись начинается и заканчивается ТОЛЬКО по действию человека, а всё её состояние лежит в ссылках:
 * перерисовка экрана (пришло сообщение, кто-то печатает) не должна перезапускать запись. В ГиперЧАТе
 * именно это однажды давало «слишком коротко» на любой длинной записи.
 *
 * Для телефона к оригиналу добавлено: выбор формата под iOS (audio/mp4) и возобновление AudioContext
 * (в WebView он создаётся «замороженным», и волна стояла бы на месте).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkTakeLength, describeMicError, LIVE_BARS, MAX_TAKE_MS, toPeaks, type VoiceTake,
} from '@/lib/voice/take';

export type CapturePhase = 'idle' | 'recording' | 'recorded';

/** Формат записи: что умеет WebView. Chrome/Android — webm/opus, Safari/iOS — mp4 (AAC). */
export function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac'].find(
    (t) => MediaRecorder.isTypeSupported(t),
  );
}

export function useVoiceCapture() {
  const [phase, setPhase] = useState<CapturePhase>('idle');
  const [levels, setLevels] = useState<number[]>(() => new Array(LIVE_BARS).fill(0));
  const [seconds, setSeconds] = useState(0);
  const [take, setTake] = useState<VoiceTake | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const ctx = useRef<AudioContext | null>(null);
  const frame = useRef(0);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = useRef(0);
  const history = useRef<number[]>([]);
  const chunks = useRef<Blob[]>([]);
  const discard = useRef(false);
  const starting = useRef(false);

  const release = useCallback(() => {
    cancelAnimationFrame(frame.current);
    if (ticker.current) clearInterval(ticker.current);
    ticker.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    void ctx.current?.close().catch(() => undefined);
    ctx.current = null;
    setLevels(new Array(LIVE_BARS).fill(0));
  }, []);

  // Уход с экрана посреди записи: микрофон гасится, иначе у человека остаётся гореть значок записи.
  useEffect(
    () => () => {
      discard.current = true;
      if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
      release();
    },
    [release],
  );

  const start = useCallback(async (): Promise<boolean> => {
    if (starting.current || recorder.current?.state === 'recording') return false;
    starting.current = true;
    setError(null);
    setTake(null);
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = media;
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(media, { mimeType: mime }) : new MediaRecorder(media);
      recorder.current = rec;
      chunks.current = [];
      history.current = [];
      discard.current = false;

      const audio = new AudioContext();
      ctx.current = audio;
      void audio.resume().catch(() => undefined);
      const analyser = audio.createAnalyser();
      analyser.fftSize = 64;
      audio.createMediaStreamSource(media).connect(analyser);
      const freq = new Uint8Array(analyser.frequencyBinCount);
      const wave = new Uint8Array(analyser.fftSize);
      let lastSample = 0;

      const draw = (now: number) => {
        analyser.getByteFrequencyData(freq);
        setLevels(Array.from(freq).slice(0, LIVE_BARS).map((v) => v / 255));
        // Громкость для формы готовой записи — десять раз в секунду.
        if (now - lastSample >= 100) {
          lastSample = now;
          analyser.getByteTimeDomainData(wave);
          let sum = 0;
          for (const v of wave) sum += ((v - 128) / 128) ** 2;
          history.current.push(Math.sqrt(sum / wave.length));
        }
        frame.current = requestAnimationFrame(draw);
      };
      frame.current = requestAnimationFrame(draw);

      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      rec.onstop = () => {
        const durationMs = Date.now() - startedAt.current;
        release();
        recorder.current = null;
        if (discard.current) {
          setPhase('idle');
          return;
        }
        const len = checkTakeLength(durationMs);
        if (!len.ok) {
          setPhase('idle');
          setError(len.error);
          return;
        }
        const type = rec.mimeType || mime || 'audio/webm';
        setTake({
          blob: new Blob(chunks.current, { type }),
          mime: type,
          durationMs,
          peaks: toPeaks(history.current),
        });
        setPhase('recorded');
      };

      startedAt.current = Date.now();
      rec.start(250);
      setSeconds(0);
      setPhase('recording');
      ticker.current = setInterval(() => {
        const elapsed = Date.now() - startedAt.current;
        setSeconds(Math.floor(elapsed / 1000));
        if (elapsed >= MAX_TAKE_MS && recorder.current?.state === 'recording') recorder.current.stop();
      }, 250);
      return true;
    } catch (e) {
      release();
      setPhase('idle');
      setError(describeMicError(e));
      return false;
    } finally {
      starting.current = false;
    }
  }, [release]);

  /** Закончить запись — дальше человек выбирает: текстом или голосом. */
  const stop = useCallback(() => {
    if (recorder.current?.state === 'recording') recorder.current.stop();
  }, []);

  /** Бросить запись или уже готовый дубль. */
  const cancel = useCallback(() => {
    discard.current = true;
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
    else release();
    setTake(null);
    setPhase('idle');
  }, [release]);

  /** Дубль забрали (отправили или распознали) — строка ввода снова обычная. */
  const clear = useCallback(() => {
    setTake(null);
    setPhase('idle');
  }, []);

  /** Идёт ли запись прямо сейчас (не состояние экрана, а настоящее состояние записи). */
  const isRecording = useCallback(() => recorder.current?.state === 'recording', []);

  return { phase, levels, seconds, take, error, setError, start, stop, cancel, clear, isRecording };
}
